import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import {
  fetchGames,
  fetchPlayoffBracket,
  fetchSeason,
  fetchSeasons,
  fetchStandings,
} from '../services/league'
import {
  Card,
  ConferenceBadge,
  DelinquencyNote,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  PlayoffBadge,
  Segmented,
  SortableHeaderRow,
  dateSortValue,
  describeDelinquency,
  formatDate,
  formatPct,
  formatRecord,
  formatSharePct,
  formatSeasonStatus,
  playerProfilePath,
  recordSortKey,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import type { GameSummary, GameTypeFilter, StandingsRow } from '../types'
import './Historical.css'

const SEASON_RECORD_COLUMNS: Array<SortableColumn<StandingsRow>> = [
  { key: 'rank', label: '#', className: 'rank' },
  { key: 'player', label: 'Player', sort: row => row.displayName },
  { key: 'conference', label: 'Conf', sortLabel: 'conference', sort: row => row.conference },
  {
    key: 'record',
    label: 'Record',
    className: 'record',
    hint: 'Sort by wins, then by fewest losses',
    sort: row => recordSortKey(row.wins, row.losses),
  },
  {
    key: 'pct',
    label: 'Pct',
    className: 'num',
    sortLabel: 'win percentage',
    sort: row => (row.gamesPlayed === 0 ? null : row.winPercentage),
  },
  {
    key: 'pointsFor',
    label: 'PF',
    className: 'num',
    sortLabel: 'points for',
    sort: row => row.pointsFor,
  },
  {
    key: 'pointsAgainst',
    label: 'PA',
    className: 'num',
    sortLabel: 'points against',
    sort: row => row.pointsAgainst,
  },
  {
    key: 'p1Pct',
    label: 'P1%',
    className: 'num',
    sortLabel: 'P1 share',
    hint: 'Share of games this player had the P1 controller',
    sort: row => (row.gamesPlayed === 0 ? null : row.p1Percentage),
  },
]

/**
 * One archived season's final table.
 *
 * @remarks
 * These rows are the combined league ranking, never a conference seeding, so
 * nothing here draws a playoff line and re-sorting cannot contradict one.
 */
function SeasonRecordsTable({ rows }: { rows: StandingsRow[] }) {
  const sort = useTableSort(rows, SEASON_RECORD_COLUMNS)

  return (
    <>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <SortableHeaderRow columns={SEASON_RECORD_COLUMNS} sort={sort} />
          </thead>
          <tbody>
            {sort.rows.map((row, index) => (
              <tr key={row.userId ?? row.displayName}>
                <td className="rank">{index + 1}</td>
                <td>
                  <PlayerCell
                    name={row.displayName}
                    discordId={row.discordId}
                    avatar={row.discordAvatar}
                    to={row.userId === null ? undefined : playerProfilePath(row.userId)}
                    flag={describeDelinquency(row.delinquency)}
                  />
                </td>
                <td>
                  <ConferenceBadge conference={row.conference} />
                </td>
                <td className="record">{formatRecord(row.wins, row.losses, row.ties)}</td>
                <td className="num">{formatPct(row.winPercentage)}</td>
                <td className="num">{row.pointsFor}</td>
                <td className="num">{row.pointsAgainst}</td>
                <td className="num" title={`${row.p1Games} of ${row.gamesPlayed} as P1`}>
                  {formatSharePct(row.gamesPlayed === 0 ? null : row.p1Percentage)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DelinquencyNote rows={sort.rows} />
    </>
  )
}

const SEASON_GAME_COLUMNS: Array<SortableColumn<GameSummary>> = [
  { key: 'date', label: 'Date', sort: game => dateSortValue(game.playedAt ?? game.uploadedAt) },
  {
    key: 'type',
    label: 'Type',
    sortLabel: 'game type',
    sort: game => (game.isPlayoff ? 1 : 0),
  },
  { key: 'home', label: 'Home', sort: game => game.home.displayName },
  {
    key: 'score',
    label: 'Score',
    className: 'num',
    hint: 'Sort by combined points',
    sortLabel: 'combined score',
    sort: game => game.home.score + game.away.score,
  },
  { key: 'away', label: 'Away', sort: game => game.away.displayName },
  { key: 'actions' },
]

function SeasonGamesTable({ games }: { games: GameSummary[] }) {
  const sort = useTableSort(games, SEASON_GAME_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={SEASON_GAME_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(game => (
            <tr key={game.id}>
              <td className="muted">{formatDate(game.playedAt ?? game.uploadedAt)}</td>
              <td>
                {game.isPlayoff ? (
                  <PlayoffBadge isPlayoff />
                ) : (
                  <span className="muted">Regular</span>
                )}
              </td>
              <td>
                <PlayerCell
                  name={game.home.displayName}
                  team={game.home.teamAbbreviation}
                  to={
                    game.home.leaguePlayerId === null
                      ? undefined
                      : playerProfilePath(game.home.leaguePlayerId)
                  }
                />
              </td>
              <td className="num">
                <strong>
                  {game.home.score}–{game.away.score}
                </strong>
              </td>
              <td>
                <PlayerCell
                  name={game.away.displayName}
                  team={game.away.teamAbbreviation}
                  to={
                    game.away.leaguePlayerId === null
                      ? undefined
                      : playerProfilePath(game.away.leaguePlayerId)
                  }
                />
              </td>
              <td className="num">
                <Link className="btn btn--ghost btn--sm" to={`/games/${game.id}`}>
                  View
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Labels for the game-type filter, in the order they read as a narrowing. */
const GAME_TYPE_OPTIONS: Array<{ value: GameTypeFilter; label: string }> = [
  { value: 'all', label: 'All games' },
  { value: 'regular', label: 'Regular season' },
  { value: 'playoffs', label: 'Playoffs' },
]

function SeasonDetailView({ seasonNumber }: { seasonNumber: number }) {
  const [gameType, setGameType] = useState<GameTypeFilter>('all')

  const detail = useAsync(() => fetchSeason(seasonNumber), [seasonNumber])
  const standings = useAsync(() => fetchStandings(seasonNumber), [seasonNumber])
  // Filtered server-side: the list is capped at 500, so narrowing the page that
  // comes back would hide games rather than find them.
  const games = useAsync(
    () => fetchGames({ season: seasonNumber, status: 'official', type: gameType, limit: 500 }),
    [seasonNumber, gameType]
  )
  const bracket = useAsync(() => fetchPlayoffBracket(seasonNumber), [seasonNumber])

  if (detail.loading) return <Loading rows={6} />
  if (detail.error) return <Notice tone="error">{detail.error}</Notice>

  const hasData = (standings.data?.overall.length ?? 0) > 0

  return (
    <div className="stack">
      <div className="grid-2">
        {detail.data?.schedule.map(progress => (
          <Card
            key={progress.conference}
            title={`${progress.conference} round robin`}
            conference={progress.conference}
          >
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <div className="stat__label">Players</div>
                <div className="stat__value">{progress.playerCount}</div>
              </div>
              <div>
                <div className="stat__label">Games played</div>
                <div className="stat__value">
                  {progress.playedGames}
                  <span className="muted" style={{ fontSize: '1rem' }}>
                    {' '}
                    / {progress.scheduledGames}
                  </span>
                </div>
              </div>
            </div>
            <div className="progress">
              <div
                className={`progress__bar progress__bar--${progress.conference.toLowerCase()}`}
                style={{
                  width: `${
                    progress.scheduledGames > 0
                      ? Math.min(100, (progress.playedGames / progress.scheduledGames) * 100)
                      : 0
                  }%`,
                }}
              />
            </div>
          </Card>
        ))}
      </div>

      <Card title={`Season ${seasonNumber} records`} flush>
        {standings.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={5} />
          </div>
        ) : hasData ? (
          <SeasonRecordsTable rows={standings.data!.overall} />
        ) : (
          <EmptyState title={`Season ${seasonNumber} has no data yet`}>
            Historical seasons are populated by an admin through the Excel import.
          </EmptyState>
        )}
      </Card>

      {bracket.data && (
        <Card
          title="Postseason"
          subtitle={
            bracket.data.champion
              ? `${bracket.data.champion.displayName} won the title.`
              : 'Bracket in progress.'
          }
          actions={
            <Link className="btn btn--ghost btn--sm" to={`/playoffs/${seasonNumber}`}>
              View bracket
            </Link>
          }
        >
          <p className="muted">
            Playoff results sit outside the table above — standings count regular-season games
            only.
          </p>
        </Card>
      )}

      <Card
        title={games.loading ? 'Games' : `${games.data?.length ?? 0} games`}
        actions={
          <Segmented options={GAME_TYPE_OPTIONS} value={gameType} onChange={setGameType} />
        }
        flush
      >
        {games.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={5} />
          </div>
        ) : games.data && games.data.length > 0 ? (
          <SeasonGamesTable games={games.data} />
        ) : (
          <div style={{ padding: 24 }}>
            <EmptyState
              title={
                gameType === 'playoffs'
                  ? 'No playoff games recorded'
                  : gameType === 'regular'
                    ? 'No regular-season games recorded'
                    : 'No games recorded'
              }
            >
              {gameType === 'playoffs'
                ? 'Playoff games from the early seasons were never imported; their brackets are entered by hand.'
                : 'Games appear here once a save state is uploaded or an admin enters one.'}
            </EmptyState>
          </div>
        )}
      </Card>
    </div>
  )
}

export default function Historical() {
  const { seasonNumber } = useParams<{ seasonNumber: string }>()
  const navigate = useNavigate()
  const seasons = useAsync(fetchSeasons, [])
  const [selected, setSelected] = useState<number | null>(
    seasonNumber ? Number(seasonNumber) : null
  )

  useEffect(() => {
    if (seasonNumber) {
      setSelected(Number(seasonNumber))
    }
  }, [seasonNumber])

  const selectedSeason = seasons.data?.find(season => season.number === selected)

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">
            {selectedSeason ? `Historical · ${formatSeasonStatus(selectedSeason.status)}` : 'Historical'}
          </span>
          <h1>{selected ? `Season ${selected}` : 'Season archive'}</h1>
          <p>
            Every season the league has played. Seasons that predate the app fill in as the
            admin imports their spreadsheets.
          </p>
        </div>
        {selected && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setSelected(null)
              navigate('/historical')
            }}
          >
            All seasons
          </button>
        )}
      </header>

      {seasons.error && <Notice tone="error">{seasons.error}</Notice>}

      {selected ? (
        <SeasonDetailView seasonNumber={selected} />
      ) : seasons.loading ? (
        <Loading rows={8} />
      ) : (
        <div className="season-grid">
          {seasons.data?.map(season => (
            <button
              key={season.number}
              type="button"
              className={`season-tile${season.isCurrent ? ' season-tile--current' : ''}${
                season.gameCount === 0 ? ' season-tile--empty' : ''
              }`}
              onClick={() => {
                setSelected(season.number)
                navigate(`/historical/${season.number}`)
              }}
            >
              <span className="season-tile__number">{season.number}</span>
              <span className="season-tile__meta">
                {formatSeasonStatus(season.status)} ·{' '}
                {season.gameCount > 0
                  ? `${season.gameCount} game${season.gameCount === 1 ? '' : 's'}`
                  : 'No data'}
              </span>
              {season.isCurrent && <span className="season-tile__flag">Current</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
